import { useState, useCallback } from 'react';
import type { WorkspaceTreeEntry } from '../api/types.js';
import { useWorkspaceTree } from '../hooks/use-workspace.js';

interface FileTreeProps {
  serverId: string;
  selectedPath: string | null;
  onSelect: (path: string, type: 'file' | 'directory') => void;
  onRefresh: () => void;
}

function getFileIcon(name: string, type: 'file' | 'directory', isOpen?: boolean): string {
  if (type === 'directory') return isOpen ? '📂' : '📁';
  const ext = name.split('.').pop()?.toLowerCase();
  const icons: Record<string, string> = {
    ts: '🟦', tsx: '🟦', js: '🟨', jsx: '🟨',
    json: '📋', yml: '⚙️', yaml: '⚙️', toml: '⚙️',
    md: '📝', txt: '📄', html: '🌐', css: '🎨',
    py: '🐍', go: '🔵', rs: '🦀', sh: '🔧', bash: '🔧',
    tf: '🏗️', hcl: '🏗️',
    lock: '🔒', env: '🔐',
    png: '🖼️', jpg: '🖼️', svg: '🖼️', gif: '🖼️',
  };
  return ext && icons[ext] ? icons[ext] : '📄';
}

function buildFullPath(parentPath: string, name: string): string {
  return parentPath ? `${parentPath}/${name}` : name;
}

interface TreeNodeProps {
  entry: WorkspaceTreeEntry;
  parentPath: string;
  serverId: string;
  selectedPath: string | null;
  depth: number;
  onSelect: (path: string, type: 'file' | 'directory') => void;
}

function TreeNode({ entry, parentPath, serverId, selectedPath, depth, onSelect }: TreeNodeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fullPath = buildFullPath(parentPath, entry.name);
  const isDir = entry.type === 'directory';
  const isSelected = selectedPath === fullPath;

  // Lazy-load children when directory is expanded
  const { data, isLoading } = useWorkspaceTree(
    serverId,
    fullPath,
    1,
  );

  // Only fetch when directory is open and has no preloaded children
  const children = entry.children ?? (isOpen && data ? data.entries : null);

  const handleClick = useCallback(() => {
    if (isDir) {
      setIsOpen((prev) => !prev);
      onSelect(fullPath, 'directory');
    } else {
      onSelect(fullPath, 'file');
    }
  }, [isDir, fullPath, onSelect]);

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm"
        style={{
          paddingLeft: `${depth * 16 + 8}px`,
          color: isSelected ? 'var(--color-brand)' : 'var(--color-text-2)',
          backgroundColor: isSelected ? 'var(--color-brand-dim)' : 'transparent',
          fontWeight: isSelected ? 500 : 400,
        }}
        title={fullPath}
      >
        {isDir && (
          <svg
            className={`h-3 w-3 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            style={{ color: 'var(--color-text-3)' }}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        )}
        {!isDir && <span className="w-3" />}
        <span className="shrink-0 text-xs">{getFileIcon(entry.name, entry.type, isOpen)}</span>
        <span className="truncate">{entry.name}</span>
      </button>

      {isDir && isOpen && (
        <div>
          {isLoading && !entry.children && (
            <div className="space-y-1 py-1" style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-4 w-24 animate-pulse rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
              ))}
            </div>
          )}
          {children && sortEntries(children).map((child) => (
            <TreeNode
              key={child.name}
              entry={child}
              parentPath={fullPath}
              serverId={serverId}
              selectedPath={selectedPath}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function sortEntries(entries: WorkspaceTreeEntry[]): WorkspaceTreeEntry[] {
  return [...entries].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export function FileTree({ serverId, selectedPath, onSelect, onRefresh }: FileTreeProps) {
  const { data, isLoading, isError, error } = useWorkspaceTree(serverId, undefined, 2);

  return (
    <div className="flex h-full flex-col" data-testid="file-tree">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-3)' }}>Files</span>
        <button
          type="button"
          onClick={onRefresh}
          className="rounded p-1"
          style={{ color: 'var(--color-text-3)' }}
          title="Refresh file tree"
          aria-label="Refresh file tree"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
            />
          </svg>
        </button>
      </div>

      {/* Tree content */}
      <div className="flex-1 overflow-y-auto py-1">
        {isLoading && (
          <div className="space-y-1 px-3 py-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-5 animate-pulse rounded" style={{ width: `${60 + i * 10}%`, backgroundColor: 'var(--color-surface-3)' }} />
            ))}
          </div>
        )}
        {isError && (
          <div className="px-3 py-4 text-center text-sm" style={{ color: 'var(--color-error)' }}>
            {error.message}
          </div>
        )}
        {data && sortEntries(data.entries).map((entry) => (
          <TreeNode
            key={entry.name}
            entry={entry}
            parentPath=""
            serverId={serverId}
            selectedPath={selectedPath}
            depth={0}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
